import { useEffect, useRef, useState } from 'react';

import { GetPdpIntegratedData_pdp_size_info_item_list, useCatalogProductIdParams } from '@domains/pdp-common';

import { useSizeInfo } from '../../../queries/useSizeInfoQuery';

export const useLegacyActualSizeService = () => {
  const language = 'ko';
  const param = useCatalogProductIdParams();
  const { data } = useSizeInfo(param);

  const overflowRef = useRef<HTMLTableElement | null>(null);
  const longestRef = useRef<HTMLSpanElement | null>(null);
  const [columnMinWidth, setColumnMinWidth] = useState<number>(84); // 기본 min-width
  const [isSmallerThanDeviceWidth, setIsSmallerThanDeviceWidth] = useState(false); // 모든 컬럼 width 합이 디바이스 너비보다 작은지 체크한 결과값

  const isOverflowing = checkIsHorizontalOverflowing(overflowRef.current);
  const itemList = toItemList(data?.item_list || []);
  const flattened = itemList?.[0]?.valueList?.[0] || [];
  const longestText = flattened.reduce((a, b) => (a.length > b.length ? a : b), '');
  const fixedColumnWidth = createFixedColumnWidthValue(language);

  useEffect(() => {
    if (!longestRef.current) return;

    const el = longestRef.current;
    const isClamped = el.scrollHeight > el.clientHeight;

    if (isClamped) {
      const newWidth = Math.ceil(columnMinWidth + 20); // padding 고려 + 여유
      setColumnMinWidth(Math.max(84, newWidth));
    }

    const totalWidth = flattened.slice(1).reduce((acc) => acc + columnMinWidth, fixedColumnWidth);

    if (window.innerWidth > totalWidth) {
      setIsSmallerThanDeviceWidth(true);
    } else {
      setIsSmallerThanDeviceWidth(false);
    }
  }, [columnMinWidth, fixedColumnWidth, flattened]);

  const handleSetColRef = (cell: string, ref: HTMLSpanElement | null) => {
    const isLongest = cell === longestText;
    if (isLongest && ref) {
      longestRef.current = ref;
    }
  };

  function createFixedColumnWidthValue(lang: 'ko' | 'en' | 'ja') {
    return lang === 'en' ? 108 : 80;
  }

  return {
    visible: itemList.length > 0,
    itemList,
    catalogProductId: param.catalog_product_id,
    overflowRef,
    isOverflowing,
    sizeTable: {
      longestRef,
      columnMinWidth: isSmallerThanDeviceWidth ? 'auto' : columnMinWidth + 'px',
      fixedColumnWidth: fixedColumnWidth + 'px',
      handleSetColRef,
    },
  };
};

function toItemList(list: GetPdpIntegratedData_pdp_size_info_item_list[]) {
  return list.map((item) => {
    return {
      imageUrl: item.image_url || '',
      valueList: item.value_list || [],
      description: item.description || [],
    };
  });
}

function checkIsHorizontalOverflowing<CurrentRef extends HTMLElement>(current: CurrentRef | null) {
  if (!current) {
    return false;
  }
  return current.scrollWidth > current.clientWidth;
}
